import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  message,
} from "antd";
import { EditOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import axios from "axios";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { BASE_API_URL } from "../../../utils/consts";
import { isLocationActive } from "../../../utils/helpers/locationVisibility";

interface EfaktureToken {
  _id: string;
  name: string;
  token: string;
  locationId?: { _id: string; name: string } | null;
  createdAt: string;
}

interface Location {
  _id: string;
  name: string;
}

const EfaktureSettings: React.FC = () => {
  const { t } = useTranslation();
  const userData = useSelector((state: any) => state.userData.user);
  const clientId = userData?.clientId?._id || userData?.clientId;

  const [tokens, setTokens] = useState<EfaktureToken[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<EfaktureToken | null>(null);
  const [form] = Form.useForm();

  const loadData = async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const [tokensRes, locRes] = await Promise.all([
        axios.get(`${BASE_API_URL}/${clientId}/efakture-tokens`),
        axios.get(`${BASE_API_URL}/${clientId}/locations`),
      ]);
      setTokens(tokensRes.data || []);
      setLocations((locRes.data || []).filter(isLocationActive));
    } catch (err) {
      message.error("Greška pri učitavanju podataka");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [clientId]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (record: EfaktureToken) => {
    setEditing(record);
    form.setFieldsValue({
      name: record.name,
      token: "",
      locationId: record.locationId?._id ?? null,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      if (editing) {
        await axios.put(
          `${BASE_API_URL}/${clientId}/efakture-tokens/${editing._id}`,
          values
        );
        message.success("Token ažuriran");
      } else {
        await axios.post(`${BASE_API_URL}/${clientId}/efakture-tokens`, values);
        message.success("Token dodan");
      }
      setModalOpen(false);
      loadData();
    } catch (err: any) {
      if (err?.errorFields) return;
      message.error(err?.response?.data?.message || "Greška pri čuvanju");
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      setLoading(true);
      await axios.delete(`${BASE_API_URL}/${clientId}/efakture-tokens/${id}`);
      message.success("Token obrisan");
      loadData();
    } catch {
      message.error("Greška pri brisanju");
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Naziv",
      dataIndex: "name",
      key: "name",
      render: (name: string) => <strong>{name}</strong>,
    },
    {
      title: "Lokacija",
      dataIndex: "locationId",
      key: "location",
      render: (loc: any) =>
        loc ? <Tag>{loc.name}</Tag> : <span style={{ color: "#9ca3af" }}>—</span>,
    },
    {
      title: "Token",
      dataIndex: "token",
      key: "token",
      render: (tok: string) => (
        <code style={{ fontSize: 12, color: "#6b7280" }}>{tok}</code>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 80,
      render: (_: any, record: EfaktureToken) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEdit(record)}
          />
          <Popconfirm
            title="Obrisati token?"
            okText="Da"
            cancelText="Ne"
            onConfirm={() => handleDelete(record._id)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="eFakture API tokeni"
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Dodaj token
        </Button>
      }
      loading={loading}
    >
      <Table
        dataSource={tokens}
        columns={columns}
        rowKey="_id"
        pagination={false}
        locale={{ emptyText: "Nema tokena. Dodajte prvi token." }}
      />

      <Modal
        title={editing ? "Uredi token" : "Novi token"}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        okText="Sačuvaj"
        cancelText="Odustani"
        confirmLoading={loading}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="name"
            label="Naziv tokena"
            rules={[{ required: true, message: "Naziv je obavezan" }]}
          >
            <Input placeholder='npr. "Beograd - Lokacija 1"' />
          </Form.Item>

          <Form.Item name="locationId" label="Lokacija (opciono)">
            <Select
              allowClear
              placeholder="Izaberite lokaciju"
              options={locations.map((l) => ({ value: l._id, label: l.name }))}
            />
          </Form.Item>

          <Form.Item
            name="token"
            label={editing ? "Novi API token (ostavite prazno da ne mijenjate)" : "API token"}
            rules={
              editing
                ? []
                : [{ required: true, message: "Token je obavezan" }]
            }
          >
            <Input.Password
              placeholder={editing ? "Unesite novi token (opciono)" : "Unesite API token"}
              autoComplete="new-password"
            />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default EfaktureSettings;
